import { httpsCallable } from "firebase/functions";
import { functions } from "@/api/firebase";
import type {
  DashboardWidget,
  AiConversation,
  AiMessage,
  AiMemory,
  DataQualityAlert,
  AlertRule,
  IntegrationBranding,
  OrgBranding,
  CalendarEvent,
  Study,
  Site,
  EdcRecord,
  EdcConnection,
  AuditEntry,
  OrgInvite,
  OnboardingStatus,
  OrganizationSummary,
  OrganizationDetail,
  BillingStatus,
  InvoiceRecord,
  ClinicalDataPoint,
  FormStatusRecord,
  AuditTrailEntry,
  VisitRecord,
  StudyGroup,
  StudyTeamMember,
  SkillData,
  SkillPresetData,
  AgentConfigDefinition,
  AgentVersion,
  AgentUsageStats,
  AgentRating,
} from "@/api/types";

// ── Helpers ────────────────────────────────────────────────

function callable<Req, Res>(name: string) {
  const fn = httpsCallable<Req, Res>(functions, name);
  return async (data: Req) => {
    const result = await fn(data);
    return result.data;
  };
}

function callableNoArgs<Res>(name: string) {
  const fn = httpsCallable<void, Res>(functions, name);
  return async () => {
    const result = await fn();
    return result.data;
  };
}

// ── Contact Form ────────────────────────────────────────────

export const submitContactForm = callable<
  { name: string; email: string; company?: string; message: string },
  { status: string }
>("submitContactForm");

// ── Semantic Search ─────────────────────────────────────────

export const semanticSearch = callable<
  {
    query: string;
    documentTypes?: string[];
    studyId?: string;
    buildId?: string;
    category?: string;
    limit?: number;
    includePublic?: boolean;
  },
  {
    results: Array<{
      chunkId: string;
      sourceCollection: string;
      sourceDocId: string;
      documentType: string;
      title: string;
      sectionTitle: string | null;
      text: string;
      score: number;
      studyId: string | null;
      category: string | null;
      isPublic: boolean;
    }>;
    totalResults: number;
    query: string;
  }
>("semanticSearch");

// ── Dashboard ──────────────────────────────────────────────

export const listDashboardWidgets = callableNoArgs<{
  widgets: DashboardWidget[];
  count: number;
}>("listDashboardWidgets");

export const refreshDashboardWidget = callable<
  { widgetId: string },
  { widgetId: string; status: string; cachedAt: string; result: unknown }
>("refreshDashboardWidget");

export const updateDashboardWidget = callable<
  { widgetId: string } & Partial<
    Pick<DashboardWidget, "title" | "description" | "displayType" | "layout" | "visibility" | "refreshIntervalMinutes" | "active">
  >,
  { widgetId: string; status: string }
>("updateDashboardWidget");

export const deleteDashboardWidget = callable<
  { widgetId: string },
  { widgetId: string; status: string }
>("deleteDashboardWidget");

// ── AI Conversations ───────────────────────────────────────

export const listAiConversations = callableNoArgs<{
  conversations: AiConversation[];
}>("listAiConversations");

export const getAiConversation = callable<
  { conversationId: string },
  { conversation: AiConversation; messages: AiMessage[] }
>("getAiConversation");

export const deleteAiConversation = callable<
  { conversationId: string },
  { status: string }
>("deleteAiConversation");

// ── AI Memory ─────────────────────────────────────────────

export const listAiMemories = callableNoArgs<{
  memories: AiMemory[];
}>("listAiMemories");

export const deleteAiMemory = callable<
  { memoryId: string },
  { status: string }
>("deleteAiMemory");

// ── Alerts ─────────────────────────────────────────────────

export const listDataAlerts = callable<
  { studyId?: string; severity?: string; includeResolved?: boolean; limit?: number },
  { alerts: DataQualityAlert[]; count: number; hasMore: boolean }
>("listDataAlerts");

export const acknowledgeAlert = callable<
  { alertId: string },
  { status: string }
>("acknowledgeAlert");

export const resolveAlert = callable<
  { alertId: string },
  { status: string }
>("resolveAlert");

export const listAlertRules = callableNoArgs<{
  rules: AlertRule[];
}>("listAlertRules");

export const createAlertRule = callable<
  Omit<AlertRule, "id" | "createdBy" | "createdAt" | "updatedAt">,
  { ruleId: string }
>("createAlertRule");

export const updateAlertRule = callable<
  { ruleId: string } & Partial<Pick<AlertRule, "name" | "enabled" | "studyIds" | "thresholds" | "notifyRoles">>,
  { status: string }
>("updateAlertRule");

export const deleteAlertRule = callable<
  { ruleId: string },
  { status: string }
>("deleteAlertRule");

// ── Calendar ───────────────────────────────────────────────

export const listCalendarEvents = callable<
  { studyId?: string; siteId?: string; status?: string; startAfter?: number; startBefore?: number; limit?: number },
  { events: CalendarEvent[]; hasMore: boolean }
>("listCalendarEvents");

export const createCalendarEvent = callable<
  { title: string; startDate: number; endDate?: number; isAllDay?: boolean; description?: string;
    location?: string; category?: string; studyId?: string; siteId?: string },
  { eventId: string; status: string }
>("createCalendarEvent");

export const updateCalendarEvent = callable<
  { eventId: string } & Partial<CalendarEvent>,
  { status: string }
>("updateCalendarEvent");

export const deleteCalendarEvent = callable<
  { eventId: string },
  { status: string }
>("deleteCalendarEvent");

// ── Organization Branding ──────────────────────────────────

export const getOrgBranding = callableNoArgs<OrgBranding>("getOrgBranding");

export const updateOrgBranding = callable<
  { logoStoragePath?: string; primaryColor?: string | null; secondaryColor?: string | null; displayName?: string | null; removeLogo?: boolean },
  { status: string }
>("updateOrgBranding");

// ── Integrations ───────────────────────────────────────────

export const getIntegrationBranding = callable<
  { category?: string },
  { integrations: IntegrationBranding[] }
>("getIntegrationBranding");

// ── EDC ────────────────────────────────────────────────────

export const configureEdcConnection = callable<
  {
    system: string;
    label: string;
    baseUrl: string;
    credentials: Record<string, string>;
    authMethod?: string;
    apiVersion?: string;
    studyId?: string;
    syncScope?: string[];
    syncSchedule?: string;
    studyFilter?: string[];
    linkedStudyId?: string;
    connectionId?: string;
    environment?: string;
  },
  { connectionId: string; status: string; message: string }
>("configureEdcConnection");

export const testEdcConnection = callable<
  { connectionId: string },
  { status: string; message: string; availableStudies?: Array<{id: string; name: string}> }
>("testEdcConnection");

export const triggerEdcSync = callable<
  { connectionId: string; scopes?: string[] },
  { syncId: string; status: string }
>("triggerEdcSync");

export const getEdcSyncStatus = callable<
  { syncId?: string },
  { syncs: Array<{ syncId: string; status: string; progress: number; startedAt: string }> }
>("getEdcSyncStatus");

// ── Clinical Data ─────────────────────────────────────────

export const listEdcConnections = callable<
  { studyId?: string },
  { connections: EdcConnection[] }
>("listEdcConnections");

export const listStudies = callable<
  { status?: string; phase?: string; edcSystem?: string; limit?: number; startAfter?: string },
  { studies: Study[]; hasMore: boolean }
>("listStudies");

export const getStudy = callable<
  { studyId: string },
  { study: Study }
>("getStudy");

export const listSites = callable<
  { studyId?: string; status?: string; country?: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { sites: Site[]; hasMore: boolean }
>("listSites");

export const listSubjects = callable<
  { studyId?: string; siteId?: string; status?: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { subjects: EdcRecord[]; hasMore: boolean }
>("listSubjects");

export const listSubjectForms = callable<
  { studyId: string; subjectId: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { forms: EdcRecord[]; hasMore: boolean }
>("listSubjectForms");

export const getSubjectForm = callable<
  { formId: string },
  { form: EdcRecord }
>("getSubjectForm");

export const listEdcQueries = callable<
  { studyId?: string; siteId?: string; status?: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { queries: EdcRecord[]; hasMore: boolean }
>("listEdcQueries");

// ── Data Views (OData) ──────────────────────────────────

export const listClinicalData = callable<
  { studyId: string; subjectId?: string; formName?: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { records: ClinicalDataPoint[]; hasMore: boolean }
>("listClinicalData");

export const listFormStatus = callable<
  { studyId: string; siteId?: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { records: FormStatusRecord[]; hasMore: boolean }
>("listFormStatus");

export const listAuditTrail = callable<
  { studyId: string; subjectId?: string; connectionIds?: string[]; limit?: number; startAfter?: string },
  { records: AuditTrailEntry[]; hasMore: boolean }
>("listAuditTrail");

// ── OpenAPI Data (visits, groups, team) ──────────────────

export const listVisits = callable<
  { studyId: string; isTemplate?: boolean; connectionIds?: string[]; limit?: number; startAfter?: string },
  { records: VisitRecord[]; hasMore: boolean }
>("listVisits");

export const listGroups = callable<
  { studyId: string; limit?: number; startAfter?: string },
  { records: StudyGroup[]; hasMore: boolean }
>("listGroups");

export const listStudyTeam = callable<
  { studyId: string; limit?: number },
  { users: StudyTeamMember[]; roles: StudyTeamMember[] }
>("listStudyTeam");

// ── EDC Export (Medrio) ──────────────────────────────────

export const requestEdcExport = callable<
  { connectionId: string; studyApiKey?: string; format: string; includeAuditTrail?: boolean; includeQueries?: boolean; includeIncomplete?: boolean },
  { jobId: string; status: string }
>("requestEdcExport");

export const pollEdcExportStatus = callable<
  { jobId: string },
  { jobId: string; status: string; progress: number; downloadUrl: string | null; message?: string; error: string | null }
>("pollEdcExportStatus");

export const downloadEdcExport = callable<
  { jobId: string },
  { downloadUrl: string; filename: string }
>("downloadEdcExport");

export const convertOdmExportToCrfsFn = callable<
  { jobId: string; studyTitle?: string },
  { buildId: string; crfCount: number; visitCount: number }
>("convertOdmExportToCrfs");

export const autoImportStudyDesignFn = callable<
  { studyId: string; connectionId: string },
  { buildId: string; crfCount: number; visitCount: number }
>("autoImportStudyDesign");

// ── Reports ───────────────────────────────────────────────

export const generateStudyReport = callable<
  { studyId: string },
  { pdf: string; filename: string }
>("generateStudyReport");

// ── Notification Preferences ──────────────────────────────

export const getNotificationPreferences = callableNoArgs<{
  emailEnabled: boolean;
  alertSeverities: string[];
  commentNotifications: boolean;
  reviewNotifications: boolean;
  artifactChangeNotifications: boolean;
}>("getNotificationPreferences");

export const updateNotificationPreferences = callable<
  { emailEnabled?: boolean; alertSeverities?: string[]; commentNotifications?: boolean; reviewNotifications?: boolean; artifactChangeNotifications?: boolean },
  { status: string }
>("updateNotificationPreferences");

// ── RBAC ───────────────────────────────────────────────────

export const assignRole = callable<
  { userId: string; role: string; studyIds?: string[]; siteIds?: string[] },
  { status: string }
>("assignRole");

export const revokeRole = callable<
  { userId: string },
  { status: string }
>("revokeRole");

export const listOrgRoles = callableNoArgs<{
  assignments: Array<{ uid: string; email: string; displayName: string; role: string }>;
  total: number;
}>("listOrgRoles");

// ── Audit Trail ──────────────────────────────────────────

export const listAuditEntries = callable<
  { category?: string; entityType?: string; performedBy?: string; startDate?: string; endDate?: string; limit?: number; startAfter?: string },
  { entries: AuditEntry[]; count: number; hasMore: boolean }
>("listAuditEntries");

export const exportAuditLog = callable<
  { startDate?: string; endDate?: string },
  { csv: string; filename: string }
>("exportAuditLog");

// ── Clinical Data Updates ────────────────────────────────

export const updateStudy = callable<
  { studyId: string; name?: string; status?: string; phase?: string; therapeuticArea?: string; indication?: string; sponsor?: string },
  { studyId: string; status: string }
>("updateStudy");

export const updateSite = callable<
  { siteId: string; status?: string; principalInvestigator?: string; city?: string; country?: string },
  { siteId: string; status: string }
>("updateSite");

export const updateQueryStatus = callable<
  { queryId: string; status: string },
  { queryId: string; status: string }
>("updateQueryStatus");

// ── Onboarding — Invitations ─────────────────────────────

export const inviteUser = callable<
  { email: string; role: string; studyIds?: string[]; siteIds?: string[] },
  { inviteId: string; token: string; status: string }
>("inviteUser");

export const acceptInvite = callable<
  { inviteToken: string },
  { orgId: string; role: string; status: string }
>("acceptInvite");

export const listPendingInvites = callableNoArgs<{
  invites: OrgInvite[];
  total: number;
}>("listPendingInvites");

export const revokeInvite = callable<
  { inviteId: string },
  { inviteId: string; status: string }
>("revokeInvite");

// ── Onboarding — Registration & Wizard ───────────────────

export const registerOrganization = callable<
  { email: string; password: string; displayName?: string; companyName: string; slug: string },
  { uid: string; orgId: string; status: string }
>("registerOrganization");

export const getOnboardingStatus = callableNoArgs<OnboardingStatus>(
  "getOnboardingStatus",
);

export const updateOrgProfile = callable<
  { companyName?: string; industry?: string; companySize?: string; website?: string; organizationType?: string },
  { status: string }
>("updateOrgProfile");

export const completeOnboarding = callableNoArgs<{ status: string }>(
  "completeOnboarding",
);

// ── Platform Admin ───────────────────────────────────────

export const listOrganizations = callableNoArgs<{
  organizations: OrganizationSummary[];
  total: number;
}>("listOrganizations");

export const getOrganization = callable<
  { organizationId: string },
  OrganizationDetail
>("getOrganization");

export const updateOrganizationStatus = callable<
  { organizationId: string; status: string },
  { organizationId: string; status: string }
>("updateOrganizationStatus");

// ── Billing ─────────────────────────────────────────────

export const getBillingStatus = callableNoArgs<BillingStatus>(
  "getBillingStatus",
);

export const createCheckoutSession = callable<
  { planTier: string },
  { url: string }
>("createCheckoutSession");

export const createPortalSession = callableNoArgs<{ url: string }>(
  "createPortalSession",
);

export const getInvoiceHistory = callableNoArgs<{
  invoices: InvoiceRecord[];
}>("getInvoiceHistory");

export const adminOverridePlan = callable<
  { organizationId: string; plan: string },
  { organizationId: string; plan: string; status: string }
>("adminOverridePlan");

// ── AI Skills ─────────────────────────────────────────────

export const listSkillsFn = callableNoArgs<{
  skills: SkillData[];
}>("listSkills");

export const getSkillPresetsFn = callableNoArgs<{
  presets: SkillPresetData[];
}>("getSkillPresets");

export const updateSkillFn = callable<
  { preset: string; enabled?: boolean; name?: string; description?: string; systemPrompt?: string; tools?: string[] },
  { skillId: string; status: string }
>("updateSkill");

export const resetSkillFn = callable<
  { preset: string },
  { skillId: string; status: string }
>("resetSkill");

// ── Site Intelligence ─────────────────────────────────

import type {
  SiteDirectoryFilters,
  SiteProfile,
  SiteCredential,
} from "@/api/site-intelligence-types";

export const getSiteDirectory = callable<
  SiteDirectoryFilters,
  { profiles: SiteProfile[]; hasMore: boolean }
>("getSiteDirectory");

export const getSiteProfileFn = callable<
  { siteProfileId: string },
  { profile: SiteProfile }
>("getSiteProfile");

export const publishSiteProfile = callable<
  { siteId: string },
  { siteProfileId: string; status: string }
>("publishSiteProfile");

export const updateSiteCredentials = callable<
  { siteProfileId: string; credentials: Partial<SiteCredential> },
  { siteProfileId: string; status: string }
>("updateSiteCredentials");

export const verifySiteVendor = callable<
  { siteProfileId: string; notes?: string },
  { siteProfileId: string; status: string }
>("verifySiteVendor");

// ── Protocol Builder ───────────────────────────────────

import type {
  ProtocolDocument,
  ProtocolVersion,
  UploadProtocolRequest,
  UploadProtocolResponse,
  StudyBuild,
  StudyBuildFull,
  StudyBuilderMessage,
  ApprovalMatrixEntry,
  ArtifactReview,
  ReviewComment,
  CrfEdgeCaseReport,
} from "@/api/protocol-types";

export const uploadProtocolFn = callable<
  UploadProtocolRequest,
  UploadProtocolResponse
>("uploadProtocol");

export const listProtocolsFn = callable<
  { limit?: number; startAfter?: string },
  { protocols: ProtocolDocument[]; hasMore: boolean }
>("listProtocols");

export const getProtocolFn = callable<
  { protocolId: string },
  { protocol: ProtocolDocument; versions: ProtocolVersion[] }
>("getProtocol");

export const uploadProtocolAmendmentFn = callable<
  { protocolId: string; storagePath: string; originalFilename: string; fileSizeBytes: number; mimeType: string; versionNumber: string; changesSummary: string },
  { protocolId: string; versionId: string; status: string }
>("uploadProtocolAmendment");

export const getStudyBuildFn = callable<
  { buildId: string },
  StudyBuildFull
>("getStudyBuild");

export const updateBuildArtifactFn = callable<
  { buildId: string; artifactType: string; artifactId: string; updates: Record<string, unknown> },
  { status: string; artifactId: string }
>("updateBuildArtifact");

export const approveStudyBuildFn = callable<
  { buildId: string },
  { status: string; buildId: string }
>("approveStudyBuild");

export const eSignStudyBuildFn = callable<
  { buildId: string; fullName: string; meaning: string; reauthToken: string },
  { status: string; signatureId: string }
>("eSignStudyBuild");

export const downloadProtocolFn = callable<
  { protocolId: string; versionId?: string },
  { downloadUrl: string; filename: string; expiresAt: string }
>("downloadProtocol");

export const verifyProtocolChecksumFn = callable<
  { protocolId: string; versionId?: string },
  { storedChecksum: string; computedChecksum: string; match: boolean; verified: boolean }
>("verifyProtocolChecksum");

// ── Study Builder ──────────────────────────────────────

export const getStudyBuilderConversationFn = callable<
  { buildId: string },
  { messages: StudyBuilderMessage[]; buildId: string }
>("getStudyBuilderConversation");

export const exportStudyBuildFn = callable<
  { buildId: string; format: "redcap" | "medidata" | "veeva" },
  { format: string; filename: string; data: string | Record<string, unknown> }
>("exportStudyBuild");

// ── Approval Workflow ───────────────────────────────────

export const getApprovalMatrixFn = callable<
  { buildId: string },
  { matrix: ApprovalMatrixEntry[] }
>("getApprovalMatrix");

export const updateApprovalMatrixFn = callable<
  { buildId: string; artifactType: string; requiredRoles: string[]; requiredApprovals: number },
  { status: string }
>("updateApprovalMatrix");

export const submitForReviewFn = callable<
  { buildId: string; artifactType: string; artifactId: string },
  { reviewId: string }
>("submitForReview");

export const reviewArtifactFn = callable<
  { buildId: string; reviewId: string; decision: string; comment?: string },
  { status: string }
>("reviewArtifact");

export const addReviewCommentFn = callable<
  { buildId: string; artifactType: string; artifactId: string; content: string; fieldPath?: string; parentCommentId?: string; reviewId?: string; mentionedUids?: string[]; referencedFields?: Array<{fieldId: string; crfId: string; domain: string; variable: string}> },
  { commentId: string }
>("addReviewComment");

export const listReviewCommentsFn = callable<
  { buildId: string; artifactType: string; artifactId: string; fieldPath?: string },
  { comments: ReviewComment[] }
>("listReviewComments");

export const resolveCommentFn = callable<
  { buildId: string; commentId: string },
  { status: string }
>("resolveComment");

export const listBuildReviewsFn = callable<
  { buildId: string },
  { reviews: ArtifactReview[] }
>("listBuildReviews");

export const analyzeCrfEdgeCasesFn = callable<
  { buildId: string; crfId: string },
  CrfEdgeCaseReport
>("analyzeCrfEdgeCases");

export const listStudyBuildsFn = callable<
  { status?: string; limit?: number; startAfter?: string },
  { builds: StudyBuild[]; hasMore: boolean }
>("listStudyBuilds");

// ── eTMF Documents ──────────────────────────────────────

import type { EtmfDocument } from "@/api/protocol-types";

export const fileEtmfDocumentFn = callable<
  { buildId: string; folderId: string; expectedDocName: string; storagePath: string; originalFilename: string; fileSizeBytes: number; mimeType: string; version?: string; description?: string; status?: string },
  { documentId: string; status: string }
>("fileEtmfDocument");

export const listEtmfDocumentsFn = callable<
  { buildId: string },
  { documents: EtmfDocument[] }
>("listEtmfDocuments");

export const suggestEtmfFilingFn = callable<
  { buildId: string; filename: string; mimeType: string },
  { folderId: string; expectedDocName: string; confidence: number; reasoning: string }
>("suggestEtmfFiling");

export const verifyEtmfChecksumFn = callable<
  { buildId: string; documentId: string },
  { storedChecksum: string; computedChecksum: string; match: boolean; verifiedAt: string }
>("verifyEtmfChecksum");

export const promoteToProdFn = callable<
  { buildId: string },
  { productionBuildId: string }
>("promoteToProd");

export const cloneToTestFn = callable<
  { buildId: string },
  { testBuildId: string }
>("cloneToTest");

export const getPublishReadinessFn = callable<
  { buildId: string },
  {
    ready: boolean;
    buildStatus: string;
    environment: string;
    signatureCount: number;
    checks: Array<{
      category: string;
      label: string;
      passed: boolean;
      detail: string;
    }>;
  }
>("getPublishReadiness");

export const getBuildHistoryFn = callable<
  { buildId: string; limit?: number; startAfter?: string },
  {
    entries: Array<{
      id: string;
      action: string;
      category: string;
      entityType: string;
      entityId: string;
      entityLabel?: string;
      buildId?: string;
      performedBy: string;
      performedByEmail?: string;
      orgId: string;
      changes?: Array<{ field: string; oldValue: string; newValue: string }>;
      metadata?: Record<string, unknown>;
      timestamp: string;
    }>;
    count: number;
    hasMore: boolean;
    summary: {
      editCount: number;
      reviewCount: number;
      signatureCount: number;
      statusTransitions: Array<{ from: string; to: string; at: string; by: string }>;
    };
  }
>("getBuildHistory");

// ── AI Provider ─────────────────────────────────────────

export const getAiProviderPreference = callableNoArgs<{
  provider: "openai" | "platform";
  model?: string;
  status: "active" | "not_configured";
}>("getAiProviderPreference");

export const saveAiProviderPreference = callable<
  { provider: "openai" | "platform"; apiKey?: string; model?: string },
  { status: string }
>("saveAiProviderPreference");

export const testAiProviderConnection = callable<
  { apiKey: string },
  { success: boolean; error?: string }
>("testAiProviderConnection");

// ── Simulation & Forecasting ──────────────────────────

import type {
  SimulationParameters,
  SimulationScenario,
  SimulationResult,
  ScenarioComparison,
} from "@/api/simulation-types";

export const listSimulationScenariosFn = callable<
  { studyId: string },
  { scenarios: SimulationScenario[] }
>("listSimulationScenarios");

export const saveSimulationScenarioFn = callable<
  { studyId: string; name: string; parameters: SimulationParameters; scenarioId?: string },
  { scenarioId: string; status: string }
>("saveSimulationScenario");

export const deleteSimulationScenarioFn = callable<
  { studyId: string; scenarioId: string },
  { status: string }
>("deleteSimulationScenario");

export const runSimulationFn = callable<
  { studyId: string; scenarioId?: string; parameters?: SimulationParameters; name?: string; studyStartDate?: string },
  { result: SimulationResult }
>("runSimulation");

export const compareScenariosFn = callable<
  { studyId: string; scenarioIds: string[]; studyStartDate?: string },
  ScenarioComparison
>("compareScenarios");

// ── Agent Config Management ─────────────────────────────

export const listAgentsFn = callableNoArgs<{
  agents: AgentConfigDefinition[];
}>("listAgents");

export const getAgentDetailFn = callable<
  { agentId: string },
  {
    agent: AgentConfigDefinition;
    versions: AgentVersion[];
    stats: AgentUsageStats;
    ratings: AgentRating[];
  }
>("getAgentDetail");

export const updateAgentDraftFn = callable<
  { agentId: string; draftConfig: { systemPrompt?: string; model?: string; maxRounds?: number; maxTokens?: number } },
  { status: string }
>("updateAgentDraft");

export const deployAgentVersionFn = callable<
  { agentId: string; changelog: string },
  { version: AgentVersion }
>("deployAgentVersion");

export const rollbackAgentVersionFn = callable<
  { agentId: string; versionId: string },
  { version: AgentVersion }
>("rollbackAgentVersion");

export const getAgentVersionHistoryFn = callable<
  { agentId: string; limit?: number; startAfter?: string },
  { versions: AgentVersion[]; hasMore: boolean }
>("getAgentVersionHistory");

export const getAgentUsageStatsFn = callable<
  { agentId: string; period?: "7d" | "30d" | "90d" },
  AgentUsageStats & { period: string }
>("getAgentUsageStats");

export const submitAgentRatingFn = callable<
  { agentId: string; usageId: string; rating: number; comment?: string },
  { ratingId: string; status: string }
>("submitAgentRating");
